import { useSnackbar } from 'notistack';
import { useApi } from '../../../shared/context';
import { ProductSubscriptionSuccessFeeTier, SubscriptionPaymentTerm } from '../../../shared/models';
import { useMutation, useQueryClient } from 'react-query';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { CerebroTextButton } from '../../../shared/components';
import { getResponseData } from '../../../shared/utils/axios';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { QueryKeys } from '../../../shared/hooks';
import { CompanySubscription } from '../../../shared/models';

interface DeleteCompanyPaymentTermDialogProps {
	companyGuid: string | undefined;
	dealGuid: string | undefined;
	subscription: CompanySubscription | null;
	term: SubscriptionPaymentTerm | null;
	close: () => void;
}

export const DeleteCompanyPaymentTermDialog: React.FC<DeleteCompanyPaymentTermDialogProps> = ({
	dealGuid,
	companyGuid,
	subscription,
	term,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const onDeleteConfirmation = () => {
		if (term !== null) deleteTerm(term);
	};

	const { mutate: deleteTerm } = useMutation<void, unknown, SubscriptionPaymentTerm>(
		termToDelete =>
			getResponseData(
				api.delete<void>(
					CerebroApiEndpoints.Company.SepcificPaymentTerm(companyGuid, subscription?.guid, termToDelete.guid)
				)
			),
		{
			onMutate: () => {
				close();
			},
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Deal.UpdateStatus(dealGuid));
				client.invalidateQueries(QueryKeys.Company.PaymentTerms(companyGuid, subscription?.guid));
				snackbar.enqueueSnackbar('Payment term successfully deleted.', { variant: 'success' });
				close();
			},
			onError: () => {
				snackbar.enqueueSnackbar('There was a problem deleting the deal subscription payment term.', {
					variant: 'error',
				});
			},
		}
	);

	return (
		<Dialog open={term !== null} onClose={close}>
			<DialogTitle>Confirm Delete</DialogTitle>
			<DialogContent>
				<DialogContentText>
					Are you sure you want to delete the company subscription payment term?
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onDeleteConfirmation}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};
